import React, { useState } from "react";
import { toast } from "sonner";
import { Camera, Video, Trash2, Loader2, ImageOff } from "lucide-react";
import { EVENT_LABELS, fmtDateTime, fileUrl, uploadJobFile } from "./jobUtils";
import api from "@/lib/api";

const DOT = { en_route: "bg-sky-500", arrival_detected: "bg-emerald-500", check_in: "bg-emerald-600", work_start: "bg-[#165a71]", pause: "bg-amber-500", resume: "bg-[#165a71]", work_complete: "bg-emerald-600", customer_approved: "bg-emerald-700", check_out: "bg-slate-500", departure_detected: "bg-slate-600", problem_reported: "bg-rose-500", time_dispute_opened: "bg-rose-500" };

export function JobTimeline({ events = [], showGps = false }) {
  if (!events.length) return <div className="text-sm text-[#252A34]/60" data-testid="job-timeline-empty">No tracking events yet.</div>;
  return (
    <ol className="relative border-l border-slate-200 ml-2 space-y-4" data-testid="job-timeline">
      {events.map((e) => (
        <li key={e.id} className="ml-4" data-testid={`job-event-${e.event_type}`}>
          <span className={`absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full ${DOT[e.event_type] || "bg-slate-400"}`} />
          <div className="flex flex-wrap items-baseline gap-x-3">
            <span className="text-sm font-medium text-[#071b22]">{EVENT_LABELS[e.event_type] || e.event_type.replace(/_/g, " ")}</span>
            <span className="text-xs text-[#252A34]/60">{fmtDateTime(e.at)}</span>
            {e.verification_method && <span className="text-[10px] uppercase tracking-wider rounded-full bg-slate-100 px-2 py-0.5 text-[#252A34]/70">{e.verification_method === "gps" && e.meta?.verified === false ? "gps · unverified" : e.verification_method}</span>}
            {e.offline_synced && <span className="text-[10px] uppercase tracking-wider rounded-full bg-amber-50 text-amber-700 px-2 py-0.5">offline sync</span>}
          </div>
          {(e.meta?.reason || e.note) && <div className="text-xs text-[#252A34]/70 mt-0.5">{e.meta?.reason}{e.meta?.reason && e.note ? " — " : ""}{e.note}</div>}
          {e.meta?.original && <div className="text-xs text-amber-700 mt-0.5">Original {fmtDateTime(e.meta.original)} → adjusted {fmtDateTime(e.meta.adjusted)} · {e.meta.reason}</div>}
          {showGps && e.coords && <div className="text-[11px] text-[#252A34]/50 mt-0.5 font-mono">{e.coords.lat?.toFixed(5)}, {e.coords.lng?.toFixed(5)}{e.accuracy_m != null ? ` · ±${Math.round(e.accuracy_m)} m` : ""}{e.distance_m != null ? ` · ${Math.round(e.distance_m)} m from job` : ""}</div>}
        </li>
      ))}
    </ol>
  );
}

export function ProofGrid({ proofs = [], canDelete, onDelete, emptyText = "No photos yet.", dark = false }) {
  if (!proofs.length) return <div className={`text-xs flex items-center gap-2 ${dark ? "text-white/60" : "text-[#252A34]/50"}`}><ImageOff className="h-4 w-4" /> {emptyText}</div>;
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="proof-grid">
      {proofs.map((p) => (
        <figure key={p.id} className="relative rounded-xl overflow-hidden border border-slate-200 bg-slate-50 aspect-square" data-testid={`proof-${p.phase}`}>
          {p.media_type === "video" ? <video src={fileUrl(p.url)} className="h-full w-full object-cover" controls />
            : p.media_type === "image" ? <img src={fileUrl(p.url)} alt={p.caption || p.phase} className="h-full w-full object-cover" loading="lazy" />
            : <div className="h-full w-full flex items-center justify-center text-[11px] p-2 text-center text-[#252A34]/70">{p.caption || p.media_type}</div>}
          {(p.label || p.caption) && <figcaption className="absolute bottom-0 inset-x-0 bg-black/55 text-white text-[10px] px-1.5 py-1 truncate">{p.label || p.caption}</figcaption>}
          {canDelete && <button onClick={() => onDelete(p)} className="absolute top-1 right-1 rounded-full bg-white/90 p-1 text-rose-600" aria-label="Remove" data-testid={`proof-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
        </figure>
      ))}
    </div>
  );
}

export function ProofUploader({ bookingId, phase, geo, onAdded, label = "Add photo", compact = false }) {
  const [busy, setBusy] = useState(false);
  const [caption, setCaption] = useState("");
  const [room, setRoom] = useState("");
  const onFiles = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      for (const f of Array.from(files)) {
        const up = await uploadJobFile(f, "job_proof");
        await api.post(`/jobs/${bookingId}/proof`, { phase, file_id: up.id, media_type: f.type.startsWith("video") ? "video" : "image", caption: caption || null, label: room || null, ...(geo?.pos || {}), device: geo?.device });
      }
      toast.success(`${files.length} ${phase} file${files.length > 1 ? "s" : ""} added`);
      setCaption("");
      onAdded && onAdded();
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.response?.data?.error || "Upload failed");
    } finally { setBusy(false); }
  };
  return (
    <div className={compact ? "" : "rounded-2xl border border-dashed border-slate-300 p-3"} data-testid={`proof-uploader-${phase}`}>
      {!compact && (
        <div className="flex flex-wrap gap-2 mb-2">
          <select value={room} onChange={(e) => setRoom(e.target.value)} className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm bg-white" data-testid={`proof-label-${phase}`}>
            <option value="">Area / label (optional)</option>
            {["Kitchen", "Bathroom", "Bedroom", "Living room", "Front yard", "Back yard", "Roof", "Vehicle interior", "Vehicle exterior", "Garage", "Other"].map((o) => <option key={o}>{o}</option>)}
          </select>
          <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Caption / note" className="flex-1 min-w-[140px] rounded-lg border border-slate-200 px-2 py-1.5 text-sm" data-testid={`proof-caption-${phase}`} />
        </div>
      )}
      <label className={`inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold cursor-pointer ${busy ? "bg-slate-200 text-slate-500" : "bg-[#071b22] text-white hover:bg-[#165a71]"} ${compact ? "w-full" : ""}`} data-testid={`proof-upload-btn-${phase}`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} {busy ? "Uploading…" : label}
        <input type="file" accept="image/*,video/*" multiple capture="environment" className="hidden" disabled={busy} onChange={(e) => onFiles(e.target.files)} data-testid={`proof-file-input-${phase}`} />
      </label>
      {!compact && <span className="ml-3 text-xs text-[#252A34]/50 inline-flex items-center gap-1"><Video className="h-3.5 w-3.5" /> Photos or video · timestamped · private</span>}
    </div>
  );
}

export function BeforeAfter({ proofs = [] }) {
  const before = proofs.filter((p) => p.phase === "before" && p.media_type === "image");
  const after = proofs.filter((p) => p.phase === "after" && p.media_type === "image");
  if (!before.length && !after.length) return null;
  const keys = Array.from(new Set([...before, ...after].map((p) => p.pair_key || "__none")));
  return (
    <div className="space-y-4" data-testid="before-after">
      {keys.map((k) => {
        const b = before.filter((p) => (p.pair_key || "__none") === k);
        const a = after.filter((p) => (p.pair_key || "__none") === k);
        const n = Math.max(b.length, a.length);
        return (
          <div key={k}>
            {k !== "__none" && <div className="text-xs font-bold uppercase tracking-[0.18em] text-[#165a71] mb-2">{b[0]?.label || a[0]?.label || k}</div>}
            <div className="space-y-2">
              {Array.from({ length: n }).map((_, i) => (
                <div key={i} className="grid grid-cols-2 gap-2">
                  <Pane title="Before" p={b[i]} />
                  <Pane title="After" p={a[i]} />
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Pane({ title, p }) {
  return (
    <div className="rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
      <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-[#252A34]/60 bg-white border-b border-slate-200">{title}</div>
      {p ? <img src={fileUrl(p.url)} alt={title} className="w-full aspect-[4/3] object-cover" loading="lazy" /> : <div className="w-full aspect-[4/3] flex items-center justify-center text-xs text-[#252A34]/40">No {title.toLowerCase()} image</div>}
    </div>
  );
}
